import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import type { ScreenProps } from './kit/types';

type ScreenModule = Record<string, ComponentType<ScreenProps>>;

// One file per SKU, loaded only on that SKU's page: 400 drawings in one chunk
// would ship every platform's page to every visitor.
const modules = import.meta.glob<ScreenModule>('./*/*Screen.tsx');

const pascal = (part: string): string =>
  part
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join('');

/** `instagram.post_comments` -> `InstagramPostCommentsScreen`, the export and
 *  file name of that SKU's drawing under `./instagram/`. */
const screenName = (sku: string): string =>
  `${sku.split('.').map(pascal).join('')}Screen`;

const screenPath = (sku: string): string => `./${sku.split('.')[0]}/${screenName(sku)}.tsx`;

const cache = new Map<string, LazyExoticComponent<ComponentType<ScreenProps>>>();

/** The drawing of the page this SKU reads, or null when it has none. */
export const screenFor = (sku: string): LazyExoticComponent<ComponentType<ScreenProps>> | null => {
  const load = modules[screenPath(sku)];
  if (!load) return null;
  const hit = cache.get(sku);
  if (hit) return hit;
  const name = screenName(sku);
  const Screen = lazy(() => load().then((mod) => ({ default: mod[name] })));
  cache.set(sku, Screen);
  return Screen;
};
